import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import hash from "object-hash";
import { cloneDeep } from "lodash";
import TeacherSelections from "../TeacherSelections";
import magnify from "./icons/magnify.png";
import studentIcon from "./icons/student.svg";
import Button from "../../blocks/Button";
import EditModal from "./EditModal";
import "./index.scss";
import FaEdit from "react-icons/lib/fa/edit";
import { addBatchAssignments } from "../../../../services/assignment/addBatchAssignments";
import { getUserData } from "../../../../services/API";
import DomainIcon from "./DomainIcon";
import {
  ASSIGNMENT_SEQUENCE,
  ASSIGNMENT_TYPE,
} from "../../../../services/assignment/constants";
import manifestHelper from "../../../../services/assignment/manifest";
import { makeStringPosessive } from "../../../../services/util";
import SIS from "Services/StudentInformationService";
import { GetBrandedAppName } from "../../../../services/BrandingService";

export default function Assignment(props) {
  const { type, students } = useSelector((state) => state.reports.assignment);

  const [editModalData, setEditModalData] = useState(null);
  const [waiting, setWaiting] = useState(false);
  // return to tracking if user refreshed (no redux data)
  useEffect(() => {
    if (!type) props.history.push("/teacher/reports/type/tracking");
  }, []);

  return (
    <React.Fragment>
      <TeacherSelections
        containerStyle={{ maxWidth: 300 }}
        backText="BACK TO TRACKING REPORT"
        onBack={props.history.goBack}
        teacherSelectionTypes={{
          disabledRostering: true,
          pageTitle: getPageTitle(),
        }}
      />
      <div className="reports-container assignment">
        <EditModal
          editModalData={editModalData}
          type={type}
          onClose={() => setEditModalData(null)}
        />
        <div className="scroll-content">
          {renderType()}
          {renderStudents()}
          {renderTasks()}
        </div>
        {renderControls()}
      </div>
    </React.Fragment>
  );

  function getPageTitle() {
    if (type === ASSIGNMENT_TYPE.BENCHMARK)
      return "Assign Benchmark Assessment";
    if (type === ASSIGNMENT_TYPE.PROGRESS_MONITORING)
      return "Assign Progress Monitor";
    if (type === ASSIGNMENT_TYPE.CALIBRATION)
      return "Assign Calibration";
  }

  function renderType() {
    let label, description;
    if (type === ASSIGNMENT_TYPE.BENCHMARK) {
      label = "BENCHMARK SCREENER";
      description = "These students will get the benchmark screener next";
    }
    if (type === ASSIGNMENT_TYPE.PROGRESS_MONITORING) {
      label = "PROGRESS MONITOR";
      description = "These students will get the progress monitor next";
    }
    if (type === ASSIGNMENT_TYPE.CALIBRATION) {
      label = "CALIBRATION";
      description = "These students will get the calibration form next";
    }
    return (
      <div>
        <div className="assessment-type">
          <img src={magnify} />
          <div>
            <div className="label">{label}</div>
            <div className="description">{description}</div>
          </div>
        </div>
      </div>
    );
  }

  function renderStudents() {
    return (
      <div className="section">
        <div className="section-info">
          <div className="section-label">STUDENT ASSESSMENT CONFIGURATION:</div>
        </div>
        {studentGroups.map((group, i) => {
          const filtered = students.filter(group.filter);
          if (filtered.length === 0) return;
          return (
            <div className="student-group" key={i}>
              <div className="label-wrapper">
                <div className="label">{group.label}</div>
              </div>
              {filtered.map((student) => (
                <Student
                  data={student}
                  key={student.studentId}
                  onClickEdit={() => setEditModalData(student)}
                  type={type}
                />
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  function renderTasks() {
    const types = [];
    if (type === ASSIGNMENT_TYPE.PROGRESS_MONITORING) {
      types.push(["assessment"]);
    } else {
      for (const s of students) {
        const { manifest, grade } = s;
        for (const m of manifest) {
          const { activityType } = m;
          if (activityType === "dyslexia"){
            if(type !== ASSIGNMENT_TYPE.CALIBRATION){
              types.push(...dyslexiaTasksByGrade[grade]);
            }else{
              types.push("calibration"); //###Use this for calibration for now. May need to develop new types
            }

          }else{
            types.push(activityType);
          }
        }
      }
    }

    return (
      <div className="section">
        <div className="section-info">
          <div className="section-label">TASKS:</div>
          <div className="section-description">
            The following tasks will appear in the upcoming assessment. These
            have been configured by your district. The Target Badges indicate
            their correspondence with the Reading Rope report.{" "}
          </div>
        </div>
        <table className="task-table">
          <thead>
            <tr>
              <th>Task</th>
              <th style={{ width: "40%" }}>Description</th>
              <th>Reading Rope Domains</th>
            </tr>
          </thead>
          <tbody>
            {[...new Set(types)].map((type, i) => {
              const task = taskLookup[type];
              const { name, description, domains } = task;
              return (
                <tr key={i}>
                  <td>
                    <div className="task-name">
                      <div className="task-number">{i + 1}</div>
                      <div>{name}</div>
                    </div>
                  </td>
                  <td>{description}</td>
                  <td>
                    {domains.map((domain) => (
                      <DomainIcon type={domain} key={domain} />
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function renderControls() {
    return (
      <div className="controls">
        <Button
          style={{ width: 160 }}
          onClick={createAssignments}
          waiting={waiting}
        >
          Create
        </Button>
        <Button type="secondary" onClick={props.history.goBack}>
          Cancel
        </Button>
      </div>
    );
  }

  async function createAssignments() {
    const manifestLookup = {}; // mapping hash(manifest): manifest
    const studentBins = {}; // mapping hash(manifest): [studentId]

    for (const s of students) {
      const { manifest, studentId } = s;
      let thisManifest = manifest;
      //Override progress monitoring here if they didn't edit
      if (type === ASSIGNMENT_TYPE.PROGRESS_MONITORING && !s.edited) {
        thisManifest = [
          { activityType: "assessment", locale: "EN_US", sequence: "CONTINUE" },
        ];
      }

      const manifestId = hash(manifest);

      manifestLookup[manifestId] = thisManifest;
      if (!studentBins[manifestId]) studentBins[manifestId] = [];
      studentBins[manifestId].push(studentId);

      // set the student.metadata.esl to true if spanish was set
      const isEsl = manifest.map((m) => m.locale).includes("ES_MX");
      if (isEsl) {
        const metadata = cloneDeep(s.metadata);
        metadata.esl = true;
        SIS.updateStudentMetadata(s.studentId, metadata);
      }
    }

    // create batch assignments...
    // one request per manifestId
    setWaiting(true);
    for (const manifestId in studentBins) {
      const manifest = manifestLookup[manifestId];
      const payload = {
        entityIdList: studentBins[manifestId],
        assignmentType: type,
        manifest,
      };

      console.log("MAKING ASSIGNMENT", payload);
      const res = await addBatchAssignments(payload);
      console.log("res", res);
    }
    props.history.goBack();
  }
}

function Student(props) {
  const { data,type } = props;
  //Make sure locales are unique since they're used as keys
  let localesInManifest = data.manifest.map((m) => m.locale);
  console.log("localesInManifest",localesInManifest)
  if(data.metadata && data.metadata.assessmentLanguageOverride){
    if(data.metadata.assessmentLanguageOverride === "ENGLISH_ONLY"){
      localesInManifest = ["EN_US"];
    }else {
        if(data.metadata.assessmentLanguageOverride === "SPANISH_ONLY"){
        localesInManifest = ["ES_MX"];
      }
    }
  }
  const locales = [...new Set(localesInManifest)];

  const sessions = manifestHelper.numBreaks(data.manifest) + 1;

  return (
    <div className="student">
      <div>
        <img src={studentIcon} />
        {data.first_name} {data.last_name}
      </div>
      <div>
        {sessions} Session{sessions > 1 && "s"}
      </div>
      <div>
        {locales.map((locale) => (
          <div key={locale} className={`locale ${locale}`}>
            {localeLookup[locale]}
          </div>
        ))}
      </div>
      {type !== ASSIGNMENT_TYPE.CALIBRATION &&
        <div>
        <div className="edit" onClick={props.onClickEdit}>
          <FaEdit /> Manage Language
        </div>
      </div>
      }
    </div>
  );
}

const localeLookup = {
  EN_US: "ENG",
  ES_MX: "ESP",
};

const studentGroups = [
  {
    label: "English Only",
    filter: (s) => {
      const locales = s.manifest.map((m) => m.locale);
      return locales.includes("EN_US") && !locales.includes("ES_MX");
    },
  },
  {
    label: "Spanish Only",
    filter: (s) => {
      const locales = s.manifest.map((m) => m.locale);
      return !locales.includes("EN_US") && locales.includes("ES_MX");
    },
  },
  {
    label: "English & Spanish",
    filter: (s) => {
      const locales = s.manifest.map((m) => m.locale);
      return locales.includes("EN_US") && locales.includes("ES_MX");
    },
  },
];

const taskLookup = {
  assessment: {
    name: "Oral Reading Fluency",
    description:
      "This task consists of reading a passage to ascertain overall fluency and other threads of mastery. The passage will be dynamically adapted to better gauge student ability.",
    domains: ["decoding", "phonemic", "recognition", "comprehension"],
  },
  nwf: {
    name: "Nonsense Word Fluency",
    description:
      "This task requires the student to read non-words in order to assess decoding skills.",
    domains: ["dyslexia", "decoding"],
  },
  spelling: {
    name: "Spelling",
    description:
      "Student ability to spell and encode will be tested with this task.",
    domains: ["dyslexia", "decoding"],
  },
  listeningComprehension: {
    name: "Listening Comprehension",
    description:
      "Students will answer questions about a story they've heard in order to gauge their listening comprehension skills.",
    domains: ["dyslexia", "comprehension"],
  },
  vocabularyScreener: {
    name: "Vocabulary",
    description:
      "This task consists of choosing a descriptor or picture that best matches the definition of a word.",
    domains: ["dyslexia", "comprehension"],
  },
  readingComprehension: {
    name: "Reading Comprehension",
    description:
      "Students will answer questions about the passage they've read to gauge their comprehension skills.",
    domains: ["comprehension"],
  },
  ran: {
    name: "RAN",
    description:
      "Rapid automized naming requires the student to identify and articulate the elements of a long list. Processing speed and accuracy are measured.",
    domains: ["dyslexia"],
  },
  letterSounds: {
    name: "Letter Sounds",
    description:
      "The student is shown a letter and asked to make the associated sound.  For vowels, several alternatives are accepted.",
    domains: ["dyslexia", "decoding"],
  },
  wordReading: {
    name: "Word Reading",
    description:
      "The student is shown the text of words, one at a time.  The student must read the word.",
    domains: ["dyslexia", "decoding"],
  },
  blending: {
    name: "Blending",
    description:
      "A video is provided of a person slowly articulating each phoneme in a word.  The students must blend the sound into the complete word accurately.",
    domains: ["dyslexia", "phonemic"],
  },
  calibration: {
    name: "Calibration",
    description:
      `The student is shown a series of items that will be used for the next generation of ${GetBrandedAppName()}'s dyslexia screener`,
    domains: ["dyslexia", "decoding"],
  },
  wordManipulation: {
    name: "Word Manipulation",
    description:
      "Students are verbally given a two-syllable word (e.g. \"baseball\") and asked to respond with one of the syllables removed. The entire activity is conducted orally without written words or images.",
    domains: ["dyslexia", "phonemic"],
  },
  nonsenseWordReplication: {
    name: "Nonsense Word Replication",
    description:
      `The student is shown a series of items that will be used for the next generation of ${GetBrandedAppName()}'s dyslexia screener`,
    domains: ["dyslexia", "phonemic"],
  }
};

const dyslexiaTasksByGrade = {
  0: ["ran", "wordManipulation", "nonsenseWordReplication", "letterSounds", "blending"],
  1: ["ran", "wordManipulation", "nonsenseWordReplication", "letterSounds", "wordReading", "blending"],
  2: ["ran", "wordReading"],
  3: ["ran", "wordReading"],
  4: ["ran", "wordReading"],
  5: ["ran", "wordReading"],
  6: ["ran", "wordReading"],
};
